import { Result } from "@/store/help-center";
import { GetContactsTimeseriesRequest, GetContactsTimeseriesResponse, querystring } from "@/services/api/sdk";

export type CorpusDocument = {
    CatalogId: number;
    CatalogName: string;
    Id: number;
    Title: string;
};

export type Catalog = {
    Id: number;
    Name: string;
};

export type Site = {
    url: string;
};

export type LessonPlan = {
    Id: number;
    Title: string;
};

export type Lesson = {
    Id: number;
    PlanId: number;
    Title: string;
    Description: string;
    Prompt: string;
    VoiceId?: string;
};

export function useApi2() {
    const baseUrl = import.meta.env.VITE_API2_URL;

    async function getJson<T>(response: Response) {
        if (!response.ok) { throw new Error(response.statusText); }
        return await response.json() as T;
    }

    async function generateHeading(userRequest: string) {
        const response = await fetch(`${baseUrl}/generate-heading`, { method: 'POST', body: JSON.stringify({ userRequest }) });
        const data = await getJson<{ content: string }>(response);

        return data.content;
    }

    async function helpCenterSearch(userRequest: string, catalogs: number[], onData: (data: Result) => void) {
        const stream = await fetch(`${baseUrl}/help-center-search`, { method: 'POST', body: JSON.stringify({ userRequest, catalogs }) });

        let buffer = '';
        const decoder = new TextDecoder();

        const reader = stream.body?.getReader()!;

        while (true) {

            const { value, done } = await reader.read();

            const payload = decoder.decode(value);
            const newBuffer = buffer + payload;

            const lines = newBuffer.split('\n\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const d = line.slice(6);
                    const data = JSON.parse(d);
                    onData(data as Result);
                }
            }

            if (done) {
                break;
            }
        }


        return null;
    }

    async function getContactsTimeseries(requestParameters: GetContactsTimeseriesRequest) {
        const queryParameters: any = {};

        if (requestParameters['filterDateMin'] != null) {
            queryParameters['filterDateMin'] = (requestParameters['filterDateMin'] as any).toISOString();
        }

        if (requestParameters['filterDateMax'] != null) {
            queryParameters['filterDateMax'] = (requestParameters['filterDateMax'] as any).toISOString();
        }

        if (requestParameters['interval'] != null) {
            queryParameters['interval'] = requestParameters['interval'];
        }

        if (requestParameters['teams'] != null) {
            queryParameters['teams'] = requestParameters['teams'];
        }
        const query = querystring(queryParameters)
        const response = await fetch(`${baseUrl}/timeseries?${query}`, { method: 'GET' });
        const data = await getJson<GetContactsTimeseriesResponse>(response);

        return data;
    }

    async function getDocuments() {
        const response = await fetch(`${baseUrl}/documents`);

        return await getJson<CorpusDocument[]>(response);
    }

    async function getCatalogs() {
        const response = await fetch(`${baseUrl}/catalogs`);

        return await getJson<Catalog[]>(response);
    }

    async function uploadDocument(file: File) {
        const data = new FormData();
        data.append('file', file);
        data.append('name', file.name);
        data.append('catalog', 'global');

        const response = await fetch(`${baseUrl}/documents`, { method: 'POST', body: data });

        return await getJson<{ instanceId: string }>(response);
    }

    async function getDocumentUploadStatus(id: string) {

        const response = await fetch(`${baseUrl}/document-uploads/${id}`);

        return await getJson<{
            status: 'queued' // means that instance is waiting to be started (see concurrency limits)
            | 'running' | 'paused' | 'errored' | 'terminated' // user terminated the instance while it was running
            | 'complete' | 'waiting' // instance is hibernating and waiting for sleep or event to finish
            | 'waitingForPause' // instance is finishing the current work to pause
            | 'unknown';
            error?: string;
            output?: object;
        }>(response);
    }

    function getDocumentUrl(id: number) {
        return `${baseUrl}/document-files/${id}`;
    }

    async function updateDocument(id: number, { catalog }: { catalog: string; }) {
        const response = await fetch(`${baseUrl}/documents/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ catalog }),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return await getJson<CorpusDocument>(response);
    }

    async function deleteDocument(id: number) {
        await fetch(`${baseUrl}/documents/${id}`, {
            method: 'DELETE'
        });
    }

    async function getSites() {
        const response = await fetch(`${baseUrl}/sites`);

        return await getJson<Site[]>(response);
    }

    async function createSite(url: string) {
        await fetch(`${baseUrl}/sites`, {
            method: 'POST',
            body: JSON.stringify({ url })
        });
    }

    async function deleteSite(url: string) {
        await fetch(`${baseUrl}/sites/${url}`, {
            method: 'DELETE'
        });
    }

    async function listLessonPlans() {
        const response = await fetch(`${baseUrl}/lesson-plans`);

        return await getJson<LessonPlan[]>(response);
    }

    async function createLessonPlan(title: string) {
        const response = await fetch(`${baseUrl}/lesson-plans`, {
            method: 'POST',
            body: JSON.stringify({ title }),
        });
        
        return await getJson<LessonPlan>(response);
    }

    async function listLessons(planId: number) {
        const response = await fetch(`${baseUrl}/lesson-plans/${planId}/lessons`);

        return await getJson<Lesson[]>(response);
    }

    async function createLesson(planId: number, title: string, description: string, prompt: string, voiceId?: string) {
        const response = await fetch(`${baseUrl}/lesson-plans/${planId}/lessons`, {
            method: 'POST',
            body: JSON.stringify({ title, description, prompt, voiceId }),
        });
        
        return await getJson<Lesson>(response);
    }

    return {
        getCatalogs,
        generateHeading,
        helpCenterSearch,
        getContactsTimeseries,
        uploadDocument,
        updateDocument,
        getDocumentUploadStatus,
        getDocuments,
        getDocumentUrl,
        deleteDocument,
        getSites,
        createSite,
        deleteSite,

        listLessonPlans,
        createLessonPlan,
        listLessons,
        createLesson
    };

}
